import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import DefaultAuth from 'layouts/auth/Default';

import illustration from 'assets/img/auth/auth.png';

import {
  createResetPasswordCodeApi,
  verifyResetPasswordCodeApi,
} from 'features/auth/auth.api';

function ForgotPassword() {
  const textColor = useColorModeValue('navy.700', 'white');
  const textColorSecondary = 'gray.400';
  const textColorDetails = useColorModeValue('navy.700', 'secondaryGray.600');
  const textColorBrand = useColorModeValue('brand.500', 'white');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    forgotPasswordStatus,
    forgotPasswordMessage,
    verifyCodeStatus,
    verifyCodeMessage,
  } = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (verifyCodeStatus === 'successful') {
      navigate('/auth/reset-password');
    }
  }, [verifyCodeStatus, navigate]);

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required.';
    if (forgotPasswordStatus === 'successful' && !code)
      newErrors.code = 'Code is required.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleForgotPassword = () => {
    if (validateForm()) {
      dispatch(createResetPasswordCodeApi({ email }));
    }
  };

  const handleVerifyCode = () => {
    if (validateForm()) {
      dispatch(verifyResetPasswordCodeApi({ email, code }));
    }
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: '100%', md: 'max-content' }}
        w="100%"
        mx={{ base: 'auto', lg: '0px' }}
        me="auto"
        h="50%"
        align="center"
        justify="center"
        mb={{ base: '30px', md: '60px' }}
        px={{ base: '25px', md: '0px' }}
        mt={{ base: '40px', md: '14vh' }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor} fontSize="36px" mb="10px">
            Forgot Password
          </Heading>
          <Text
            mb="36px"
            ms="4px"
            color={textColorSecondary}
            fontWeight="400"
            fontSize="md"
          >
            {forgotPasswordStatus === 'successful'
              ? 'Enter the code sent to your email.'
              : 'Enter your email to receive a password reset code.'}
          </Text>
        </Box>
        <Flex
          zIndex="2"
          direction="column"
          w={{ base: '100%', md: '420px' }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: 'auto', lg: 'unset' }}
          me="auto"
          mb={{ base: '20px', md: 'auto' }}
        >
          <FormControl isInvalid={errors.email}>
            <FormLabel
              display="flex"
              ms="4px"
              fontSize="sm"
              fontWeight="500"
              color={textColor}
              mb="8px"
            >
              Email<Text color={textColorBrand}>*</Text>
            </FormLabel>
            <Input
              isRequired={true}
              variant="auth"
              fontSize="sm"
              ms={{ base: '0px', md: '0px' }}
              type="email"
              placeholder="mail@simmmple.com"
              mb="20px"
              fontWeight="500"
              size="lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {errors.email && (
              <Text color="red.500" fontSize="sm" mb="8px">
                {errors.email}
              </Text>
            )}
            {forgotPasswordStatus !== 'successful' ? (
              <>
                <Button
                  fontSize="sm"
                  variant="brand"
                  fontWeight="500"
                  w="100%"
                  h="50"
                  mb="18px"
                  isLoading={forgotPasswordStatus === 'pending'}
                  loadingText="Sending Email..."
                  onClick={handleForgotPassword}
                >
                  Send Reset Code
                </Button>
              </>
            ) : (
              <>
                <FormLabel
                  display="flex"
                  ms="4px"
                  fontSize="sm"
                  fontWeight="500"
                  color={textColor}
                  mb="8px"
                >
                  Code<Text color={textColorBrand}>*</Text>
                </FormLabel>
                <Input
                  isRequired={true}
                  variant="auth"
                  fontSize="sm"
                  ms={{ base: '0px', md: '0px' }}
                  type="text"
                  placeholder="Enter the code"
                  mb="20px"
                  fontWeight="500"
                  size="lg"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                {errors.code && (
                  <Text color="red.500" fontSize="sm" mb="8px">
                    {errors.code}
                  </Text>
                )}
                <Button
                  fontSize="sm"
                  variant="brand"
                  fontWeight="500"
                  w="100%"
                  h="50"
                  mb="18px"
                  isLoading={verifyCodeStatus === 'pending'}
                  loadingText="Verifying Code..."
                  onClick={handleVerifyCode}
                >
                  Verify Code
                </Button>
              </>
            )}
          </FormControl>
          {forgotPasswordMessage && (
            <Text color="red.500" fontSize="sm" mb="8px">
              {forgotPasswordMessage}
            </Text>
          )}
          {verifyCodeMessage && (
            <Text color="red.500" fontSize="sm" mb="8px">
              {verifyCodeMessage}
            </Text>
          )}
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="start"
            maxW="100%"
            mt="0px"
          >
            <Text color={textColorDetails} fontWeight="400" fontSize="14px">
              Remembered your password?
              <NavLink to="/auth/sign-in">
                <Text color={textColorBrand} as="span" ms="5px" fontWeight="500">
                  Sign In
                </Text>
              </NavLink>
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default ForgotPassword;
